import type { ReactNode } from 'react';
import { useTabTitle } from './tabTitle';

export interface SystemMessageProps {
    /** 11 px uppercase line above the title ("404 · Not found"). */
    eyebrow?: ReactNode;
    /** The h1 ("There is nothing at this address"). */
    title: ReactNode;
    /** One or two sentences under it. */
    text?: ReactNode;
    /** The address that was asked for, shown in mono ("Requested /spaces/42/pages/7"). */
    requested?: string;
    /** 24 px glyph inside the dotted ring; default the orbit drawing. */
    icon?: ReactNode;
    /** Buttons: one primary, then quiet ones. */
    actions?: ReactNode;
    /** `document.title` ("Not found" → "Not found | Ark"). Default: the title when it is a string. */
    tabTitle?: string;
    className?: string;
}

function Orbit() {
    return (
        <svg width="40" height="40" viewBox="0 0 40 40" fill="none" aria-hidden="true" focusable="false">
            <circle cx="20" cy="20" r="8" stroke="currentColor" strokeWidth="1.5" />
            <ellipse cx="20" cy="20" rx="17" ry="7" transform="rotate(-20 20 20)" stroke="var(--ow-accent)" strokeWidth="1.5" />
            <circle cx="34" cy="13" r="2" fill="var(--ow-accent)" />
        </svg>
    );
}

/**
 * DS gap: the centred system message of the Shell-NotFound / Shell-Forbidden boards (not found, Identity unavailable,
 * no access): a glyph in a dotted ring, an eyebrow, the display title, text, the requested address and the actions
 * (`ds.css` "system message"). Sets `document.title`.
 */
export function SystemMessage(props: SystemMessageProps) {
    useTabTitle(props.tabTitle ?? (typeof props.title === 'string' ? props.title : undefined));
    return (
        <section className={['ark-system', props.className].filter(Boolean).join(' ')} aria-labelledby="ark-system-title">
            <div className="ark-system-art" aria-hidden="true">
                {props.icon ? <span className="ark-system-glyph">{props.icon}</span> : <Orbit />}
            </div>
            {props.eyebrow ? <span className="ark-system-eyebrow">{props.eyebrow}</span> : null}
            <h1 id="ark-system-title">{props.title}</h1>
            {props.text ? <p className="ark-system-text">{props.text}</p> : null}
            {props.requested ? <p className="ark-system-requested">Requested <code>{props.requested}</code></p> : null}
            {props.actions ? <div className="ark-system-actions">{props.actions}</div> : null}
        </section>
    );
}
